import type { selectSubscriptionInfo } from '@saaga/shared/store/slices/billing.slice'

import type { Notification } from '@/components/notifications/notification.interface'
import type { CreateOrganizationFormData } from '@/components/org-settings/org-settings.interface'

import { buildAppLayoutHandlers } from './app-layout-handlers.helper'
import { buildAppLayoutState } from './app-layout-state.helper'
import type {
  GlobalSearchResultGroup,
  GlobalSearchResultItem,
  GlobalSearchResultType,
} from './global-search.interface'
import type { SidebarNavGroup } from './sidebar-nav-item.interface'
import { type ChatDeleteDialogState, useAppLayoutActions } from './use-app-layout-actions.hook'
import { useAppLayoutNavGroups } from './use-app-layout-nav-groups.hook'
import { useAppLayoutNavigation } from './use-app-layout-navigation.hook'
import type { CreateOrganizationDialogState } from './use-app-layout-organization.hook'
import { useAppLayoutOrganization } from './use-app-layout-organization.hook'
import { useAppLayoutSearch } from './use-app-layout-search.hook'
import { useSidebarConversations } from './use-sidebar-conversations.hook'
import { useSidebarRecentlyUsedAgencies } from './use-sidebar-recently-used-agencies.hook'

export interface AppLayoutState {
  searchQuery: string
  searchResultGroupList: GlobalSearchResultGroup[]
  isSearching: boolean
  navGroups: SidebarNavGroup[]
  organizations: Array<{
    id: string
    name: string
    slug: string | null
    imageUrl: string | null
  }>
  userData: {
    name: string
    email: string
    imageUrl: string | null
  }
  activeOrgData: {
    id: string
    name: string
    slug: string | null
    imageUrl: string | null
  } | null
  subscriptionInfo: ReturnType<typeof selectSubscriptionInfo>
  createOrgDialog: CreateOrganizationDialogState
  chatDeleteDialog: ChatDeleteDialogState
}

export interface AppLayoutHandlers {
  handleSignOut: () => void
  handleOrganizationSelect: (orgId: string) => void
  handleCreateOrganization: () => void
  handleCreateOrgDialogChange: (isOpen: boolean) => void
  handleCreateOrgSubmit: (data: CreateOrganizationFormData) => void
  handleCreateProject: () => void
  handleAgenciesClick: () => void
  handleAgenciesSettings: () => void
  handleCreateChat: () => void
  handleConnectsClick: () => void
  handleOrgSettings: () => void
  handleGlobalSearch: (query: string) => void
  handleResultSelect: (type: GlobalSearchResultType, item: GlobalSearchResultItem) => void
  handleNotificationClick: (notification: Notification) => void
  handleMarkAllRead: () => void
}

export interface UseAppLayoutReturn {
  state: AppLayoutState
  handlers: AppLayoutHandlers
}

export const useAppLayout = (): UseAppLayoutReturn => {
  const navigation = useAppLayoutNavigation()
  const search = useAppLayoutSearch()
  const organization = useAppLayoutOrganization()
  const conversations = useSidebarConversations()
  const agencies = useSidebarRecentlyUsedAgencies()

  const actions = useAppLayoutActions({
    conversationPinStateMap: conversations.conversationPinStateMap,
  })

  const navGroups = useAppLayoutNavGroups({
    isPathActive: navigation.isPathActive,
    handleAgenciesSettings: navigation.handleAgenciesSettings,
    handleCreateChat: navigation.handleCreateChat,
    agentActions: actions.state.agentActions,
    chatActions: actions.state.chatActions,
    chatEditState: actions.state.chatEditState,
    pinnedConversationItemList: conversations.pinnedConversationItemList,
    conversationItemList: conversations.conversationItemList,
    agencyItemList: agencies.agencyItemList,
  })

  return {
    state: buildAppLayoutState({ search, organization, actions, navGroups }),
    handlers: buildAppLayoutHandlers({ search, organization, navigation, actions }),
  }
}
